import React, { useState, useRef, useEffect } from 'react';
import '../style/aboutus.css';
import ContactUs from './contactus';
import { FaPlay, FaPause, FaCode, FaPalette, FaRocket, FaUsers, FaAward, FaLightbulb, FaShieldAlt, FaGlobe, FaArrowRight, FaCheck, FaStar, FaQuoteLeft, FaLinkedin, FaTwitter, FaInstagram, FaVideo, FaHeadset, FaCogs, FaMedal, FaClock, FaEye, FaHandshake, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import profile1 from '../../images/hammad1.jpg';
import profile2 from '../../images/sheraz.jpg';

const AboutUs = () => {
    const [activeProcess, setActiveProcess] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [hoveredTeam, setHoveredTeam] = useState(null);
    // Add this useEffect to your AboutUs component for scroll animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            },
            {
                threshold: 0.3,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const processSteps = document.querySelectorAll('.process-step');
        processSteps.forEach(step => observer.observe(step));

        return () => observer.disconnect();
    }, []);

    // Introduction Data
    const introData = {
        title: "REDEFINING VISUAL EXCELLENCE",
        subtitle: "Where Innovation Meets Imagination",
        description: "404 FX Visuals is a premier visual production studio pushing the boundaries of digital creativity. We specialize in cutting-edge VFX, CGI, and motion graphics that transform ideas into breathtaking visual experiences for global clients.",
        stats: [
            { number: "500+", label: "Projects Completed" },
            { number: "50+", label: "Global Clients" },
            { number: "99%", label: "Client Satisfaction" },
            { number: "24/7", label: "Global Support" }
        ]
    };

    // Floating Cards Data
    const floatingCards = [
        { icon: <FaStar />, text: "Award Winning Quality" },
        { icon: <FaUsers />, text: "Expert Team" },
        { icon: <FaAward />, text: "Proven Excellence" },
        { icon: <FaVideo />, text: "Cinema Quality" },
        { icon: <FaRocket />, text: "Fast Delivery" },
        { icon: <FaGlobe />, text: "Worldwide Service" },
        { icon: <FaCogs />, text: "Advanced Technology" },
        { icon: <FaMedal />, text: "Industry Leaders" }
    ];

    // Team Members Data
    const teamMembers = [
        {
            id: 1,
            name: "Hammad",
            role: "Creative Director & VFX Supervisor",
            image: profile1,
            description: "15+ years in visual effects industry. Former lead at major  studios.",
            hoverDescription: "Led teams on blockbuster films and AAA game titles. Specializes in complex VFX pipelines and creative direction for large-scale productions.",
            specialties: ["VFX", "Creative Direction", "Pipeline Development"],
            contact: {
                email: "hammad@404fxvisuals.com",
                phone: "+1 (555) 123-4567",
                location: "Los Angeles, CA"
            }
        },
        {
            id: 2,
            name: "Sheraz",
            role: "Technical Director & CGI Specialist",
            image: profile2,
            description: "Pioneer in real-time rendering and virtual production technologies.",
            hoverDescription: "Expert in Unreal Engine and real-time CGI. Developed innovative virtual production workflows adopted by major studios worldwide.",
            specialties: ["Real-time CGI", "Technical Direction", "R&D Innovation"],
            contact: {
                email: "sarah@404fxvisuals.com",
                phone: "+1 (555) 123-4568",
                location: "San Francisco, CA"
            }
        }
    ];

    // Additional Team Info
    const teamStats = [
        { number: "50+", label: "Years Combined Experience" },
        { number: "100+", label: "Industry Awards" },
        { number: "24/7", label: "Team Collaboration" },
        { number: "Global", label: "Talent Network" }
    ];

    // Process Steps Data
    const processSteps = [
        {
            step: "01",
            title: "DISCOVERY & CONSULTATION",
            description: "Deep dive into your vision, objectives, and technical requirements. We analyze your project scope and define success metrics.",
            hoverContent: "We conduct comprehensive workshops to understand your brand, target audience, and project goals. Our team creates detailed project briefs and technical specifications.",
            icon: <FaLightbulb />,
            duration: "1-3 Days",
            deliverables: ["Project Brief", "Technical Analysis", "Timeline Estimate", "Budget Planning"],
            features: ["Stakeholder Interviews", "Market Research", "Technical Assessment"]
        },
        {
            step: "02",
            title: "CONCEPT DEVELOPMENT",
            description: "Our creative team develops concepts, storyboards, and visual prototypes. We iterate until the vision aligns perfectly.",
            hoverContent: "Our artists create multiple concept directions, style frames, and animatics. We present visual options and refine based on your feedback until perfect alignment.",
            icon: <FaPalette />,
            duration: "3-7 Days",
            deliverables: ["Concept Art", "Storyboards", "Style Frames", "Mood Boards"],
            features: ["Creative Brainstorming", "Visual Exploration", "Client Collaboration"]
        },
        {
            step: "03",
            title: "PRE-PRODUCTION PLANNING",
            description: "Detailed technical planning, asset preparation, and pipeline setup. We ensure everything is ready for smooth production.",
            hoverContent: "We establish production pipelines, create asset lists, and prepare technical specifications. Our team sets up project management systems and communication protocols.",
            icon: <FaCode />,
            duration: "2-5 Days",
            deliverables: ["Technical Script", "Asset List", "Production Schedule", "Pipeline Setup"],
            features: ["Technical Planning", "Asset Preparation", "Workflow Optimization"]
        },
        {
            step: "04",
            title: "PRODUCTION & EXECUTION",
            description: "Core production phase where magic happens. Our team creates stunning visuals using industry-leading tools and techniques.",
            hoverContent: "Our specialists execute VFX, CGI, animation, and compositing. We maintain regular progress updates and quality checks throughout the production phase.",
            icon: <FaRocket />,
            duration: "7-21 Days",
            deliverables: ["Visual Effects", "CGI Elements", "Animation Sequences", "3D Models"],
            features: ["VFX Production", "CGI Creation", "Animation Development", "Quality Control"]
        },
        {
            step: "05",
            title: "QUALITY ASSURANCE",
            description: "Rigorous testing and refinement process. We ensure every pixel meets our quality standards and client expectations.",
            hoverContent: "Multiple review cycles with technical and creative quality checks. We test across different platforms and devices to ensure optimal performance and visual quality.",
            icon: <FaShieldAlt />,
            duration: "2-4 Days",
            deliverables: ["Quality Reports", "Revision Notes", "Final Approval", "Technical Documentation"],
            features: ["Quality Testing", "Client Reviews", "Technical Optimization", "Final Polishing"]
        },
        {
            step: "06",
            title: "DELIVERY & SUPPORT",
            description: "Final delivery in required formats with ongoing support. We ensure seamless integration and project success.",
            hoverContent: "We deliver final assets in all required formats and resolutions. Our team provides technical support, documentation, and post-project consultation.",
            icon: <FaGlobe />,
            duration: "1-2 Days",
            deliverables: ["Final Files", "Documentation", "Support Package", "Project Archive"],
            features: ["Asset Delivery", "Technical Support", "Project Documentation", "Ongoing Maintenance"]
        }
    ];

    // Auto-play process steps
    useEffect(() => {
        if (!isPlaying) return;

        const interval = setInterval(() => {
            setActiveProcess(prev => (prev + 1) % processSteps.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isPlaying, processSteps.length]);

    return (
        <div className="about-us-page">
            {/* Section 1: Introduction */}
            <section className="intro-section">
                <div className="intro-background">
                    <div className="floating-shapes">
                        <div className="shape shape-1"></div>
                        <div className="shape shape-2"></div>
                        <div className="shape shape-3"></div>
                    </div>
                </div>

                <div className="intro-container">
                    <div className="intro-content">
                        <div className="intro-badge">
                            <span>EST. 2020</span>
                        </div>

                        <h1 className="intro-title">
                            {introData.title}
                        </h1>

                        <h2 className="intro-subtitle">
                            {introData.subtitle}
                        </h2>

                        <p className="intro-description">
                            {introData.description}
                        </p>
                    </div>

                    <div className="intro-visual">
                        <div className="floating-cards-grid">
                            {floatingCards.map((card, index) => (
                                <div
                                    key={index}
                                    className={`floating-card card-${index + 1}`}
                                >
                                    <div className="card-icon">{card.icon}</div>
                                    <span>{card.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Stats Section - Single Row */}
                <div className="stats-section">
                    <div className="stats-container">
                        {introData.stats.map((stat, index) => (
                            <div key={index} className="stat-item">
                                <div className="stat-number">{stat.number}</div>
                                <div className="stat-label">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section 2: Our Team */}
            {/* <section className="team-section">
                <div className="section-header">
                    <h2 className="section-title">MEET OUR VISIONARIES</h2>
                    <p className="section-subtitle">The brilliant minds behind extraordinary visuals</p>
                </div> */}

                {/* Team Stats */}
                {/* <div className="team-stats">
                    {teamStats.map((stat, index) => (
                        <div key={index} className="team-stat-item">
                            <div className="team-stat-number">{stat.number}</div>
                            <div className="team-stat-label">{stat.label}</div>
                        </div>
                    ))}
                </div> */}

                {/* <div className="team-grid">
                    {teamMembers.map((member, index) => (
                        <div
                            key={member.id}
                            className="team-card"
                            onMouseEnter={() => setHoveredTeam(member.id)}
                            onMouseLeave={() => setHoveredTeam(null)}
                        >
                            <div className="card-inner">
                                <div className={`card-front ${hoveredTeam === member.id ? 'hidden' : ''}`}>
                                    <div className="member-image">
                                        <img src={member.image} alt={member.name} />
                                        <div className="image-overlay"></div>
                                    </div>
                                    <div className="member-info">
                                        <h3 className="member-name">{member.name}</h3>
                                        <p className="member-role">{member.role}</p>
                                        <p className="member-description">{member.description}</p>
                                        <div className="specialties">
                                            {member.specialties.map((specialty, idx) => (
                                                <span key={idx} className="specialty-tag">{specialty}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className={`card-back ${hoveredTeam === member.id ? 'visible' : ''}`}>
                                    <FaQuoteLeft className="quote-icon" />
                                    <p className="member-hover-description">{member.hoverDescription}</p>
                                    <div className="member-contact">
                                        <div className="contact-item">
                                            <FaEnvelope className="contact-icon" />
                                            <span>{member.contact.email}</span>
                                        </div>
                                        <div className="contact-item">
                                            <FaPhone className="contact-icon" />
                                            <span>{member.contact.phone}</span>
                                        </div>
                                        <div className="contact-item">
                                            <FaMapMarkerAlt className="contact-icon" />
                                            <span>{member.contact.location}</span>
                                        </div>
                                    </div>
                                    <div className="specialties">
                                        {member.specialties.map((specialty, idx) => (
                                            <span key={idx} className="specialty-tag">{specialty}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div> */}
            {/* </section> */}

            {/* Section 3: Our Process - Updated Center Alignment */}
            <section className="process-section">
                <div className="section-header">
                    <h2 className="section-title">OUR CREATIVE PROCESS</h2>
                    <p className="section-subtitle">A meticulous journey from concept to masterpiece</p>

                    <div className="process-controls">
                        <button
                            className="control-btn"
                            onClick={() => setIsPlaying(!isPlaying)}
                        >
                            {isPlaying ? <FaPause /> : <FaPlay />}
                            {isPlaying ? 'Pause' : 'Play'} Auto-scroll
                        </button>
                    </div>
                </div>

                <div className="process-timeline">
                    <div className="timeline-line"></div>

                    {processSteps.map((step, index) => (
                        <div
                            key={index}
                            className={`process-step ${activeProcess === index ? 'active' : ''}`}
                            onClick={() => {
                                setActiveProcess(index);
                                setIsPlaying(false);
                            }}
                        >
                            <div className="step-marker">
                                <div className="step-number">{step.step}</div>
                                <div className="step-icon">{step.icon}</div>
                            </div>

                            <div className="step-content">
                                <div className="step-header">
                                    <h3 className="step-title">{step.title}</h3>
                                    <span className="step-duration">{step.duration}</span>
                                </div>

                                <p className="step-description">{step.description}</p>

                                <div className="step-hover-content">
                                    <p>{step.hoverContent}</p>
                                </div>

                                <div className="step-deliverables">
                                    <h4>DELIVERABLES:</h4>
                                    <ul>
                                        {step.deliverables.map((item, idx) => (
                                            <li key={idx}>
                                                <FaCheck className="check-icon" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="step-features">
                                    <h4>KEY FEATURES:</h4>
                                    <div className="features-tags">
                                        {step.features.map((feature, idx) => (
                                            <span key={idx} className="feature-tag">{feature}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            {/* Section 4: Contact Us */}
            <section className="contact-section">
                <ContactUs />
            </section>
        </div>
    );
};

export default AboutUs;